import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Tooltip
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  CheckCircle as OnlineIcon,
  Cancel as OfflineIcon,
  PlayArrow as DispatchEnabledIcon,
  Pause as DispatchDisabledIcon,
  Security as SafetyIcon,
  Warning as UnsafeIcon,
  HelpOutline as SafetyUnknownIcon,
  CameraAlt as ObservingIcon,
  ToggleOn as AutomationEnabledIcon,
  // Phase and state icons
  RocketLaunch as StartupIcon,
  AcUnit as CooldownIcon,
  CheckCircleOutline as ReadyIcon,
  NightsStay as ShutdownIcon,
  Visibility as ObservingStateIcon,
  Build as MaintenanceIcon,
  Thunderstorm as WeatherUnsafeIcon,
  // Session state icons
  WbSunny as DaytimeIcon,
  WbTwilight as TwilightIcon
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useActiveReservations, reservationHeldUntil } from '../../hooks/useActiveReservation';
import { websocketService } from '../../services/websocketService';
import EquipmentDetailCards from '../../components/operator/EquipmentDetailCards';

const ObservatoryList = () => {
  // One request for all rows; a reserved observatory is not simply idle.
  const reservationsByObservatory = useActiveReservations();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // When opened via a ?focus=<id> URL (e.g. the admin Control Center's
  // observatory row click), render just that one observatory's card.
  const focusId = searchParams.get('focus');
  const [observatories, setObservatories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadObservatories();

    // Subscribe to real-time telemetry updates
    const handleTelemetryUpdate = (data) => {
      console.log('[ObservatoryList] Received telemetry update:', data);

      // Update the specific observatory in the list
      setObservatories(prevObs => {
        return prevObs.map(obs => {
          if (obs._id === data.observatory_id) {
            return {
              ...obs,
              equipment_status: data.equipment_status || obs.equipment_status,
              system_status: data.system_status || obs.system_status,
              last_updated: data.timestamp
            };
          }
          return obs;
        });
      });
    };

    // Subscribe to observatory status updates (connection/disconnection)
    const handleObservatoryUpdate = (data) => {
      console.log('[ObservatoryList] Received observatory update:', data);

      setObservatories(prevObs => {
        return prevObs.map(obs => {
          // Match by observatory ID or name
          if (obs._id === data.observatory_id || obs.name === data.observatory_name) {
            const updates = {};

            if (data.status !== undefined) {
              updates.status = data.status;
            }

            if (data.plugin_connected !== undefined) {
              updates.status = data.plugin_connected ? 'online' : 'offline';
            }

            return { ...obs, ...updates };
          }
          return obs;
        });
      });
    };

    // Subscribe to observation updates (to track current observation)
    const handleObservationUpdate = (data) => {
      console.log('[ObservatoryList] Received observation update:', data);

      setObservatories(prevObs => {
        return prevObs.map(obs => {
          // Update current observation if this observatory is involved
          if (obs._id === data.observatory_id) {
            const updates = { ...obs };

            // Update current observation based on status
            if (data.status === 'assigned' || data.status === 'in_progress') {
              // Set/update current observation with all available details
              updates.current_observation = {
                observation_id: data.observation_id,
                name: data.name,
                target_name: data.target_name,
                status: data.status,
                completion_percentage: data.completion_percentage || 0
              };
            } else if (data.status === 'completed' || data.status === 'failed' || data.status === 'cancelled' || data.status === 'aborted') {
              // Clear current observation when finished
              updates.current_observation = null;
            } else if (data.type === 'progress_update' && data.progress_percent !== undefined) {
              // Update progress
              if (updates.current_observation) {
                updates.current_observation.completion_percentage = data.progress_percent;
              }
            }

            return updates;
          }
          return obs;
        });
      });
    };

    websocketService.onTelemetryUpdate(handleTelemetryUpdate);
    websocketService.onObservatoryUpdate(handleObservatoryUpdate);
    websocketService.onObservationUpdate(handleObservationUpdate);

    // Cleanup subscriptions on unmount. All three must be removed — the
    // websocketService listener arrays outlive this component (singleton),
    // so leaving observatory/observation handlers registered makes every
    // remount stack another listener and log/handle each update N times.
    return () => {
      websocketService.offTelemetryUpdate(handleTelemetryUpdate);
      websocketService.offObservatoryUpdate(handleObservatoryUpdate);
      websocketService.offObservationUpdate(handleObservationUpdate);
    };
  }, []);

  const loadObservatories = async () => {
    try {
      setLoading(true);
      const response = await apiService.getObservatories();
      setObservatories(response.data.observatories || []);
      setError(null);
    } catch (err) {
      console.error('Failed to load observatories:', err);
      setError('Failed to load observatories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status) => {
    return status === 'online' ? <OnlineIcon /> : <OfflineIcon />;
  };

  const getDispatchIcon = (enabled) => {
    return enabled ? <DispatchEnabledIcon /> : <DispatchDisabledIcon />;
  };

  const getDispatchColor = (enabled) => {
    return enabled ? 'success' : 'default';
  };

  const getSafetyIcon = (safetyMonitor) => {
    if (!safetyMonitor?.connected) {
      return <SafetyUnknownIcon />;
    }
    return safetyMonitor.is_safe ? <SafetyIcon /> : <UnsafeIcon />;
  };

  const getSafetyColor = (safetyMonitor) => {
    if (!safetyMonitor?.connected) {
      return 'default';
    }
    return safetyMonitor.is_safe ? 'success' : 'error';
  };

  const getSafetyLabel = (safetyMonitor) => {
    if (!safetyMonitor?.connected) {
      return 'No Safety Monitor';
    }
    return safetyMonitor.is_safe ? 'Safe' : 'Unsafe';
  };

  const getAutomationIcon = (enabled) => {
    return enabled ? <AutomationEnabledIcon /> : <DispatchDisabledIcon />;
  };

  const getAutomationColor = (enabled) => {
    return enabled ? 'success' : 'warning';
  };

  const getAutomationLabel = (enabled) => {
    return enabled ? 'Automation' : 'Manual';
  };

  // Get operational state display info (simplified v2 model)
  const getOperationalStateInfo = (state) => {
    const states = {
      // Auto/derived states
      unknown: { icon: SafetyUnknownIcon, label: 'Unknown', color: 'default' },
      observing: { icon: ObservingStateIcon, label: 'Observing', color: 'success' },
      // Manual states (now implemented)
      dusk_startup: { icon: StartupIcon, label: 'Dusk Startup', color: 'info' },
      dusk_cooldown: { icon: CooldownIcon, label: 'Dusk Cooldown', color: 'info' },
      ready: { icon: ReadyIcon, label: 'Ready', color: 'success' },
      weather_hold: { icon: WeatherUnsafeIcon, label: 'Weather Hold', color: 'warning' },
      shutdown: { icon: ShutdownIcon, label: 'Shutdown', color: 'warning' },
      error: { icon: UnsafeIcon, label: 'Error', color: 'error' },
      // Future states (calibration)
      dusk_flats: { icon: OnlineIcon, label: 'Dusk Flats', color: 'info' },
      dusk_calibration: { icon: OnlineIcon, label: 'Dusk Calibration', color: 'info' },
      bad_weather_calibration: { icon: OnlineIcon, label: 'Bad Weather Cal', color: 'warning' },
      dawn_flats: { icon: OnlineIcon, label: 'Dawn Flats', color: 'info' },
      dawn_calibration: { icon: OnlineIcon, label: 'Dawn Calibration', color: 'info' },
      // Legacy states (for backwards compatibility)
      // Note: 'online'/'offline' are connection states, not operational states - excluded
      // 'idle' is excluded - it's a meaningless legacy state
      maintenance: { icon: MaintenanceIcon, label: 'Maintenance', color: 'warning' },
      weather_unsafe: { icon: WeatherUnsafeIcon, label: 'Weather Unsafe', color: 'error' },
      equipment_error: { icon: UnsafeIcon, label: 'Equipment Error', color: 'error' },
    };
    return states[state] || null;
  };

  // Get session state display info (calculated from sun position).
  // Daytime needs an explicit sx override — MUI's `default` color renders
  // grey, which reads wrong for a midday indicator; force the chip yellow
  // (matches ObservatoryDetailsBase's own daytime chip).
  const getSessionStateInfo = (sessionState) => {
    const sessions = {
      daytime: { icon: DaytimeIcon, label: 'Daytime', color: 'default', sx: { backgroundColor: '#ffeb3b', color: '#000' } },
      dusk: { icon: TwilightIcon, label: 'Dusk', color: 'info' },
      night: { icon: ShutdownIcon, label: 'Night', color: 'primary' },
      dawn: { icon: TwilightIcon, label: 'Dawn', color: 'warning' },
    };
    return sessions[sessionState] || null;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  const visibleObservatories = focusId
    ? observatories.filter((o) => (o._id || o.id) === focusId)
    : observatories;

  return (
    <Box p={3}>
      {!focusId && (
        <Box mb={3}>
          <Typography variant="h4" gutterBottom>
            Observatories
          </Typography>
          <Typography variant="body1" color="text.secondary">
            View observatory schedules and availability
          </Typography>
        </Box>
      )}

      {visibleObservatories.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {focusId ? 'Observatory not found' : 'No observatories available'}
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {visibleObservatories.map((observatory) => (
            <Grid item xs={12} md={focusId ? 12 : 6} lg={focusId ? 12 : 4} key={observatory._id}>
              <Card
                elevation={focusId ? 0 : 1}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: focusId ? 'default' : 'pointer',
                  // When focused (admin-control-center pop-out), blend with
                  // the floating window / page background instead of
                  // standing out as a raised paper tile.
                  ...(focusId && {
                    backgroundColor: 'transparent',
                    backgroundImage: 'none',
                    border: 'none',
                  }),
                  '&:hover': focusId ? undefined : { boxShadow: 4 },
                }}
                onClick={focusId ? undefined : () => navigate(`/observatories/${observatory._id}`)}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                    <Typography variant="h6" component="div">
                      {observatory.name}
                    </Typography>
                    {/* Status Badges - unified with Dashboard and ObservatoryDetailsBase */}
                    <Box display="flex" gap={1} flexWrap="wrap">
                      <Chip
                        icon={getStatusIcon(observatory.status)}
                        label={observatory.status || 'unknown'}
                        color={getStatusColor(observatory.status)}
                        size="small"
                      />
                      {/* Session State chip (calculated from sun position) - only show when online */}
                      {observatory.status === 'online' && observatory.session_state && (() => {
                        const sessionInfo = getSessionStateInfo(observatory.session_state);
                        if (!sessionInfo) return null;
                        const SessionIcon = sessionInfo.icon;
                        return (
                          <Tooltip title="Session state calculated from sun position">
                            <Chip
                              icon={<SessionIcon />}
                              label={sessionInfo.label}
                              color={sessionInfo.color}
                              size="small"
                              sx={sessionInfo.sx}
                            />
                          </Tooltip>
                        );
                      })()}
                      {/* Operational State chip (signaled by plugin) - only show when online */}
                      {observatory.status === 'online' && observatory.operational_state && (() => {
                        const stateInfo = getOperationalStateInfo(observatory.operational_state);
                        if (!stateInfo) return null;
                        const StateIcon = stateInfo.icon;
                        return (
                          <Tooltip title={`Operational State: ${stateInfo.label}`}>
                            <Chip
                              icon={<StateIcon />}
                              label={stateInfo.label}
                              color={stateInfo.color}
                              size="small"
                            />
                          </Tooltip>
                        );
                      })()}
                      {/* Only show operational chips when online */}
                      {observatory.status === 'online' && (
                        <>
                          <Chip
                            icon={getDispatchIcon(observatory.dispatch_enabled !== false)}
                            label={observatory.dispatch_enabled !== false ? 'Dispatching' : 'Paused'}
                            color={getDispatchColor(observatory.dispatch_enabled !== false)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            icon={getSafetyIcon(observatory.equipment_status?.safetyMonitor)}
                            label={getSafetyLabel(observatory.equipment_status?.safetyMonitor)}
                            color={getSafetyColor(observatory.equipment_status?.safetyMonitor)}
                            size="small"
                            variant="outlined"
                          />
                          <Chip
                            icon={getAutomationIcon(observatory.system_status?.operations_enabled !== false)}
                            label={getAutomationLabel(observatory.system_status?.operations_enabled !== false)}
                            color={getAutomationColor(observatory.system_status?.operations_enabled !== false)}
                            size="small"
                            variant="outlined"
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  {observatory.description && (
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {observatory.description}
                    </Typography>
                  )}

                  {!observatory.current_observation?.target_name
                    && !observatory.current_observation?.name
                    && reservationsByObservatory[String(observatory._id || observatory.id)] && (() => {
                    const reservation = reservationsByObservatory[String(observatory._id || observatory.id)];
                    const until = reservationHeldUntil(reservation);
                    return (
                      <Box
                        display="flex" alignItems="center" gap={1} mb={2} p={1.5}
                        sx={{
                          backgroundColor: 'rgba(255,152,0,0.08)',
                          borderLeft: '4px solid',
                          borderColor: 'warning.main',
                          borderRadius: 1,
                        }}
                      >
                        <ObservingIcon color="warning" fontSize="small" />
                        <Box>
                          <Typography variant="caption" display="block" color="warning.main" sx={{ fontWeight: 700 }}>
                            RESERVED — not available for scheduling
                          </Typography>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {reservation.title}
                          </Typography>
                          {until && (
                            <Typography variant="caption" color="text.secondary">
                              Held until {until}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    );
                  })()}

                  {(observatory.current_observation?.target_name || observatory.current_observation?.name) && (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      mb={2}
                      p={1.5}
                      sx={{
                        backgroundColor: 'primary.50',
                        borderLeft: '4px solid',
                        borderColor: 'primary.main',
                        borderRadius: 1
                      }}
                    >
                      <ObservingIcon color="primary" fontSize="small" />
                      <Box>
                        <Typography variant="caption" display="block" color="primary" sx={{ fontWeight: 600 }}>
                          Currently Observing
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {observatory.current_observation.target_name || observatory.current_observation.name}
                        </Typography>
                        {observatory.current_observation.completion_percentage > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {observatory.current_observation.completion_percentage}% complete
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}

                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {observatory.location?.latitude?.toFixed(2)}°, {observatory.location?.longitude?.toFixed(2)}°
                    </Typography>
                  </Box>

                  {observatory.location?.timezone && (
                    <Typography variant="body2" color="text.secondary">
                      <strong>Timezone:</strong> {observatory.location.timezone}
                    </Typography>
                  )}

                  {focusId ? (
                    <Box mt={2} onClick={(e) => e.stopPropagation()}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        Equipment Status
                      </Typography>
                      <EquipmentDetailCards
                        observatory={observatory}
                        telemetry={{ equipment_status: observatory.equipment_status }}
                        compact
                      />
                    </Box>
                  ) : (
                    <>
                      {observatory.configuration?.telescope_config?.focal_length_mm && (() => {
                        const fl = observatory.configuration.telescope_config.focal_length_mm;
                        const ap = observatory.configuration.telescope_config.aperture_mm;
                        // Aperture sometimes arrives as a reverse-computed
                        // float (e.g. 430 / 7). Round for display; keep one
                        // decimal only when the value genuinely isn't integer.
                        const apLabel = (ap == null)
                          ? null
                          : (Math.abs(ap - Math.round(ap)) < 0.05
                              ? Math.round(ap).toString()
                              : ap.toFixed(1));
                        const fRatio = ap ? (fl / ap).toFixed(1) : null;
                        return (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            <strong>Telescope:</strong>{' '}
                            {apLabel ? `${apLabel}mm` : '—'}{fRatio ? ` f/${fRatio}` : ''}
                            {' '}({fl}mm)
                          </Typography>
                        );
                      })()}

                      {observatory.capabilities?.available_filters && (
                        <Box mt={2}>
                          <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                            Available Filters:
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {observatory.capabilities.available_filters.map((filter) => (
                              <Chip key={filter} label={filter} size="small" variant="outlined" />
                            ))}
                          </Box>
                        </Box>
                      )}
                    </>
                  )}

                  {observatory.capabilities?.available_instruments && observatory.capabilities.available_instruments.length > 0 && (
                    <Box mt={1}>
                      <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                        Instruments:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {observatory.capabilities.available_instruments.map((instrument) => (
                          <Chip key={instrument} label={instrument} size="small" variant="outlined" />
                        ))}
                      </Box>
                    </Box>
                  )}
                  {observatory.branding?.live_feed_url && (
                    <Box
                      mt={2}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        borderRadius: 1,
                        overflow: 'hidden',
                        border: '1px solid rgba(0,0,0,0.12)',
                        backgroundColor: '#000',
                      }}
                    >
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ px: 1, py: 0.5, backgroundColor: 'rgba(255,255,255,0.04)' }}>
                        Live Feed
                      </Typography>
                      <Box
                        component="iframe"
                        title={`${observatory.name} live feed`}
                        src={observatory.branding.live_feed_url}
                        allow="autoplay; fullscreen; picture-in-picture"
                        sx={{
                          width: '100%',
                          height: Math.min(observatory.branding.live_feed_height || 240, 280),
                          border: 'none',
                          display: 'block',
                        }}
                      />
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default ObservatoryList;
